import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Search,
  Bell,
  ShieldCheck,
  ChevronDown,
  Crosshair,
  Compass,
  FileDown,
  RotateCcw,
  Settings,
  LogOut,
} from 'lucide-react';
import { useActiveFocus, useData, useUI } from '@/data/stores';
import { Badge } from '@/components';
import { severityColor } from '@/lib/color';
import { demoTimestamp, downloadJson } from '@/lib/export';
import './shell.css';

const VIEW_TITLES: Record<string, string> = {
  '/': 'National COP',
  '/map': 'Geospatial COP',
  '/globe': '3D Globe',
  '/assets': 'Asset Registry & Digital Twins',
  '/alerts': 'Alerts & Incidents',
  '/scenarios': 'Scenario Modelling & Simulation',
  '/ot': 'OT Monitoring & Active Defence',
  '/intel': 'Intelligence & Knowledge Graph',
  '/compliance': 'Compliance & Reporting',
  '/settings': 'Settings & System',
};

export function TopBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const alerts = useData((s) => s.alerts);
  const assets = useData((s) => s.assets);
  const reports = useData((s) => s.reports);
  const actors = useData((s) => s.actors);
  const vessels = useData((s) => s.vessels);
  const focusModes = useData((s) => s.focusModes);
  const activeFocus = useActiveFocus();
  const setFocusMode = useUI((s) => s.setFocusMode);
  const startTour = useUI((s) => s.startTour);
  const notify = useUI((s) => s.notifyAction);
  const [focusOpen, setFocusOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);

  const titleKey = useMemo(() => {
    const match = Object.keys(VIEW_TITLES).find(
      (k) => k !== '/' && location.pathname.startsWith(k),
    );
    return match ?? location.pathname;
  }, [location.pathname]);
  const title = VIEW_TITLES[titleKey] ?? 'Iris';

  const openAlerts = alerts.filter(
    (a) => a.status === 'open' || a.status === 'investigating',
  );
  const top = openAlerts.reduce<number>(
    (best, a) => (a.severity > best ? a.severity : best),
    0,
  );

  const [q, setQ] = useState('');
  const [showResults, setShowResults] = useState(false);
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        ref.current?.focus();
        setShowResults(true);
      }
      if (e.key === 'Escape') {
        setShowResults(false);
        ref.current?.blur();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const results = useMemo(() => {
    const lower = q.trim().toLowerCase();
    if (!lower) return null;
    const a = assets
      .filter(
        (x) =>
          x.name.toLowerCase().includes(lower) ||
          x.asset_id.toLowerCase().includes(lower) ||
          x.owner_operator.toLowerCase().includes(lower) ||
          x.country.toLowerCase().includes(lower) ||
          x.tags.some((t) => t.toLowerCase().includes(lower)),
      )
      .slice(0, 8);
    const al = alerts
      .filter(
        (x) =>
          x.title.toLowerCase().includes(lower) ||
          x.description.toLowerCase().includes(lower) ||
          x.alert_id.toLowerCase().includes(lower) ||
          x.asset_ids.some((id) => id.toLowerCase().includes(lower)) ||
          (x.threat_actor?.toLowerCase().includes(lower) ?? false),
      )
      .slice(0, 5);
    const v = vessels
      .filter(
        (x) =>
          x.name.toLowerCase().includes(lower) ||
          x.mmsi.toLowerCase().includes(lower) ||
          (x.imo?.toLowerCase().includes(lower) ?? false) ||
          x.flag.toLowerCase().includes(lower),
      )
      .slice(0, 5);
    const r = reports
      .filter(
        (x) =>
          x.title.toLowerCase().includes(lower) ||
          x.body.toLowerCase().includes(lower) ||
          x.report_id.toLowerCase().includes(lower) ||
          x.source.toLowerCase().includes(lower),
      )
      .slice(0, 5);
    const t = actors
      .filter(
        (x) =>
          x.name.toLowerCase().includes(lower) ||
          x.actor_id.toLowerCase().includes(lower) ||
          x.aliases.some((alias) => alias.toLowerCase().includes(lower)) ||
          x.target_sectors.some((sector) => sector.toLowerCase().includes(lower)),
      )
      .slice(0, 5);
    return { a, al, v, r, t };
  }, [q, assets, alerts, vessels, reports, actors]);

  return (
    <header className="iris-topbar">
      <div className="iris-topbar-left">
        <div className="iris-wordmark">IRIS</div>
        <div className="iris-divider" />
        <div className="iris-topbar-title">{title}</div>
      </div>
      <div className="iris-topbar-search">
        <Search size={14} strokeWidth={1.5} />
        <input
          ref={ref}
          placeholder="Search assets, alerts, entities… (⌘K)"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setShowResults(true);
          }}
          onFocus={() => setShowResults(true)}
          onBlur={() => setTimeout(() => setShowResults(false), 200)}
        />
        <kbd className="iris-kbd">⌘K</kbd>
        {showResults &&
          results &&
          (results.a.length > 0 ||
            results.al.length > 0 ||
            results.v.length > 0 ||
            results.r.length > 0 ||
            results.t.length > 0) && (
          <div className="iris-search-dropdown">
            {results.a.length > 0 && (
              <>
                <div className="iris-search-group">Assets</div>
                {results.a.map((a) => (
                  <button
                    key={a.asset_id}
                    className="iris-search-item"
                    onMouseDown={() => {
                      navigate(`/assets/${a.asset_id}`);
                      setShowResults(false);
                      setQ('');
                    }}
                  >
                    <span>{a.name}</span>
                    <span className="mono iris-search-meta">{a.asset_id}</span>
                  </button>
                ))}
              </>
            )}
            {results.al.length > 0 && (
              <>
                <div className="iris-search-group">Alerts</div>
                {results.al.map((a) => (
                  <button
                    key={a.alert_id}
                    className="iris-search-item"
                    onMouseDown={() => {
                      navigate(`/alerts/${a.alert_id}`);
                      setShowResults(false);
                      setQ('');
                    }}
                  >
                    <span>{a.title}</span>
                    <span className="mono iris-search-meta">{a.alert_id}</span>
                  </button>
                ))}
              </>
            )}
            {results.v.length > 0 && (
              <>
                <div className="iris-search-group">Vessels</div>
                {results.v.map((v) => (
                  <button
                    key={v.mmsi}
                    className="iris-search-item"
                    onMouseDown={() => {
                      navigate(`/intel?q=${encodeURIComponent(v.name)}`);
                      setShowResults(false);
                      setQ('');
                    }}
                  >
                    <span>{v.name}</span>
                    <span className="mono iris-search-meta">MMSI {v.mmsi}</span>
                  </button>
                ))}
              </>
            )}
            {results.t.length > 0 && (
              <>
                <div className="iris-search-group">Threat actors</div>
                {results.t.map((actor) => (
                  <button
                    key={actor.actor_id}
                    className="iris-search-item"
                    onMouseDown={() => {
                      navigate(`/intel?q=${encodeURIComponent(actor.name)}`);
                      setShowResults(false);
                      setQ('');
                    }}
                  >
                    <span>{actor.name}</span>
                    <span className="mono iris-search-meta">{actor.actor_id}</span>
                  </button>
                ))}
              </>
            )}
            {results.r.length > 0 && (
              <>
                <div className="iris-search-group">Intelligence</div>
                {results.r.map((report) => (
                  <button
                    key={report.report_id}
                    className="iris-search-item"
                    onMouseDown={() => {
                      navigate(`/intel?q=${encodeURIComponent(report.title)}`);
                      setShowResults(false);
                      setQ('');
                    }}
                  >
                    <span>{report.title}</span>
                    <span className="mono iris-search-meta">{report.source}</span>
                  </button>
                ))}
              </>
            )}
          </div>
        )}
      </div>
      <div className="iris-topbar-right">
        <button
          className="iris-topbar-tour-button"
          onClick={() => {
            setFocusOpen(false);
            setUserOpen(false);
            startTour();
            notify({
              kind: 'success',
              title: 'Platform tour started',
              body: 'Use Next and Back to move through the hosted demo walkthrough.',
            });
          }}
        >
          <Compass size={14} strokeWidth={1.5} />
          <span>See How Iris Works</span>
        </button>
        <div
          className="iris-topbar-focus"
          onClick={() => {
            setUserOpen(false);
            setFocusOpen((v) => !v);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setUserOpen(false);
              setFocusOpen((v) => !v);
            }
          }}
          role="button"
          tabIndex={0}
        >
          <Crosshair size={12} strokeWidth={1.5} />
          <span className="mono">{activeFocus.short_label}</span>
          <ChevronDown size={11} strokeWidth={1.5} />
          {focusOpen && (
            <div className="iris-focus-dropdown" onClick={(e) => e.stopPropagation()}>
              <div className="iris-focus-dropdown-title">Focus mode</div>
              {focusModes.map((m) => (
                <button
                  key={m.id}
                  className={`iris-focus-dropdown-item ${m.id === activeFocus.id ? 'on' : ''}`}
                  onMouseDown={() => {
                    setFocusMode(m.id);
                    setFocusOpen(false);
                    setUserOpen(false);
                    notify({
                      kind: 'success',
                      title: `Focus mode: ${m.label}`,
                      body: location.pathname.startsWith('/map')
                        ? 'Map auto-fit queued for the selected operating area.'
                        : 'All views are now filtered to this operating picture.',
                    });
                  }}
                >
                  <span className="mono iris-focus-dropdown-shortlabel">{m.short_label}</span>
                  <span>{m.label}</span>
                </button>
              ))}
              <div className="iris-focus-dropdown-foot caption">
                Configure in Settings → Focus modes
              </div>
            </div>
          )}
        </div>
        <div className="iris-topbar-pill" title="Posture: elevated (CMP-2026-EU-ENERGY-Q2)">
          <ShieldCheck size={14} strokeWidth={1.5} />
          <span>POSTURE: ELEVATED</span>
        </div>
        <button
          className="iris-topbar-bell"
          onClick={() => {
            setFocusOpen(false);
            setUserOpen(false);
            navigate('/alerts');
          }}
          aria-label="Alerts"
        >
          <Bell size={16} strokeWidth={1.5} />
          {openAlerts.length > 0 && top > 0 && (
            <Badge color={severityColor(top as 1 | 2 | 3 | 4 | 5)}>
              {openAlerts.length}
            </Badge>
          )}
        </button>
        <div className="iris-topbar-user-wrap">
          <button
            className="iris-topbar-user"
            onClick={() => {
              setFocusOpen(false);
              setUserOpen((v) => !v);
            }}
            aria-haspopup="menu"
            aria-expanded={userOpen}
          >
            <span className="iris-user-avatar">DU</span>
            <span className="iris-user-meta">
              <span>Demo User</span>
              <span className="iris-user-role">Demo Analyst</span>
            </span>
            <ChevronDown size={14} strokeWidth={1.5} />
          </button>
          {userOpen && (
            <div className="iris-user-dropdown" role="menu">
              <div className="iris-user-dropdown-head">
                <div className="iris-user-dropdown-title">Demo analyst session</div>
                <div className="iris-user-dropdown-meta">
                  Demo Analyst · {activeFocus.short_label}
                </div>
              </div>
              <button
                role="menuitem"
                onClick={() => {
                  setUserOpen(false);
                  navigate('/settings');
                }}
              >
                <Settings size={14} strokeWidth={1.5} />
                Open settings
              </button>
              <button
                role="menuitem"
                onClick={() => {
                  setUserOpen(false);
                  startTour();
                  notify({
                    kind: 'success',
                    title: 'Platform tour started',
                    body: 'Use Next and Back to move through the hosted demo walkthrough.',
                  });
                }}
              >
                <Compass size={14} strokeWidth={1.5} />
                Start tour
              </button>
              <button
                role="menuitem"
                onClick={() => {
                  downloadJson(`iris-demo-session-${demoTimestamp()}.json`, {
                    generated_at: new Date().toISOString(),
                    user: {
                      name: 'Demo User',
                      role: 'Demo Analyst',
                    },
                    focus_mode: activeFocus,
                    posture: 'elevated',
                    counts: {
                      assets: assets.length,
                      open_alerts: openAlerts.length,
                      intelligence_reports: reports.length,
                    },
                  });
                  setUserOpen(false);
                  notify({
                    kind: 'success',
                    title: 'Session summary exported',
                    body: 'The hosted demo state, focus, posture, and counts were downloaded as JSON.',
                  });
                }}
              >
                <FileDown size={14} strokeWidth={1.5} />
                Export session
              </button>
              <button
                role="menuitem"
                onClick={() => {
                  setFocusMode('global');
                  setUserOpen(false);
                  notify({
                    kind: 'warning',
                    title: 'Demo session reset',
                    body: 'Focus returned to Global. Locally staged demo records remain available in this browser session.',
                  });
                }}
              >
                <RotateCcw size={14} strokeWidth={1.5} />
                Reset session
              </button>
              <button
                role="menuitem"
                onClick={async () => {
                  setUserOpen(false);
                  await fetch('/api/logout', {
                    method: 'POST',
                    credentials: 'same-origin',
                  }).catch(() => undefined);
                  window.dispatchEvent(new Event('iris-demo-logout'));
                }}
              >
                <LogOut size={14} strokeWidth={1.5} />
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
